import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import gsap from 'gsap'

import { MotionRoot } from '@/components/generals/motion-root'
import { prefersReducedMotion } from '@/lib/motion'

type PageTransitionProps = {
    children: ReactNode
}

/**
 * Route changes cross-fade behind a full-bleed veil, the way the original site
 * hands one page to the next: the outgoing page dims and lifts, the veil closes,
 * then the incoming page settles back down.
 */
export const PageTransition: FC<PageTransitionProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const veilRef = useRef<HTMLDivElement>(null)
    const holderRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const veil = veilRef.current
        const holder = holderRef.current

        if (!veil || !holder) return

        if (prefersReducedMotion()) return

        const onStart = (url: string): void => {
            if (url.split('#')[0] === router.asPath.split('#')[0]) return

            gsap.killTweensOf([veil, holder])
            gsap.set(veil, { pointerEvents: 'auto' })
            gsap.to(veil, { opacity: 1, duration: 0.28, ease: 'power2.out' })
            gsap.to(holder, { opacity: 0.4, y: -14, duration: 0.28, ease: 'power2.out' })
        }

        const onDone = (): void => {
            gsap.killTweensOf([veil, holder])
            gsap.set(holder, { y: 14, opacity: 0 })
            gsap.to(holder, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' })
            gsap.to(veil, {
                opacity: 0,
                duration: 0.45,
                ease: 'power2.out',
                onComplete: () => {
                    gsap.set(veil, { pointerEvents: 'none' })
                }
            })
        }

        router.events.on('routeChangeStart', onStart)
        router.events.on('routeChangeComplete', onDone)
        router.events.on('routeChangeError', onDone)

        return () => {
            router.events.off('routeChangeStart', onStart)
            router.events.off('routeChangeComplete', onDone)
            router.events.off('routeChangeError', onDone)
        }
    }, [router])

    return (
        <>
            <div
                ref={veilRef}
                aria-hidden='true'
                className='pointer-events-none fixed inset-0 z-[120] bg-[#0d0d0d] opacity-0'
            />

            <div ref={holderRef}>
                <MotionRoot>
                    {children}
                </MotionRoot>
            </div>
        </>
    )
}
